import {
  FileUploadStatusT,
  FormattingOption,
  InputRichText,
  InputRichTextProps,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import EmojiOne from 'emojione';
import { ConversationsChannels } from 'lib-shared-legacy-types';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import noop from 'lodash/noop';
import * as React from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { arrayPush } from 'redux-form';

import { filesApi } from 'apis/files';
import { getFileSize } from 'app/utils/attachments';
import {
  channelToSanitizationProfile,
  SanitizeHTML,
} from 'app/utils/htmlSanitizer';
import markdownToHTML from 'app/utils/markdownToHTML';
import supportedLanguages, {
  DEFAULT_LANG_CODE,
} from 'app/utils/supportedLanguages';
import { getCurrentConversationLanguage } from 'components/conversation/selectors';
import * as personalActions from 'components/settings/actions/personal';
import {
  CommandGroup,
  commandGroupDelimiter,
  useCommands,
} from 'utils/commands';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { StateBar } from './components/stateBar/stateBar';
import type { ValidationState } from './components/stateBar/stateBar.types';
import styles from './richTextField.module.scss';
import { useWhatsAppExclusiveMode } from './utils/useWhatsAppExclusiveMode';
import { validateWhatsAppAttachment } from './utils/validateWhatsAppAttachment';

const { personalSubmitMessageWithEnter } = personalActions.actions;

const MAX_ATTACHMENTS_TOTAL_FILE_SIZE = 20097152; // ~20mb.
const MAX_INLINE_FILE_SIZE = 100000000; // ~100mb.
let formIdCounter = 0;

export type Channel = ConversationsChannels | 'notes' | 'bulk' | 'none';

export type RichTextFieldProps = {
  value?: string;
  channel: Channel;
  sanitize?: (html: string) => string;
  language?: string;
  disableSubmitMessageWithEnter?: boolean;
  requesterName?: string;
  className?: string;
  stateBarClassName?: string;
  label?: string;
  name?: string;
  required?: boolean;
  placeholder?: string;
  attachments?: File[];
  attachmentsFields?: any;
  onFileIsUploadingChange?: (state: boolean) => void;
  /** Redux form name */
  form?: string;
  onChange?: (value: string) => void;
  onTriggerSend?: () => void;
  setValidationState?: (validation: ValidationState) => void;
  validation?: any;
  editorWindow?: any;
  hasBorder?: boolean;
  id?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  autocomplete?: {
    hasAutocompleteSuggestionsFeature: boolean;
    showAutocompleteSuggestions: boolean;
    onToggleShowAutocompleteSuggestions: () => void;
  };
  hideSendOnEnterToggle?: boolean;
  isPublicReply?: boolean;
  toolbarAddons?: React.ReactNode;
  disableAttachments?: boolean;
  disableInlineAttachments?: boolean;
  disableEmoji?: boolean;
};

export type RichTextFieldRef = {
  getText: () => string;
  getPlainText: () => string;
  setText: (text: string) => void;
  setMarkdown: (text: string) => void;
  focus: () => void;
  moveCursorToEnd: () => void;
};

const RichTextField = forwardRef<RichTextFieldRef, RichTextFieldProps>(
  function RichTextField(
    {
      value,
      channel,
      sanitize,
      disableSubmitMessageWithEnter = false,
      hideSendOnEnterToggle,
      requesterName,
      placeholder,
      id,
      className,
      label,
      required,
      attachmentsFields,
      attachments,
      setValidationState,
      editorWindow = window,
      validation,
      hasBorder,
      autocomplete,
      stateBarClassName,
      isPublicReply,
      toolbarAddons,
      form,
      onChange,
      onKeyDown = noop,
      onTriggerSend,
      onFileIsUploadingChange,
      disableAttachments,
      disableInlineAttachments,
      disableEmoji,
    },
    ref,
  ): JSX.Element {
    const editorRef = useRef<InputRichText | null>(null);

    const dispatch = useAppDispatch();
    const flags = useFeatureFlag([
      'write-html-email',
      'templates-language-translation-control',
      'conversation-whatsapp-rich-messages',
      'conversation-whatsapp-outbound-attachments',
    ]);
    const { keybindings } = useCommands();

    const submitMessageWithEnter = useAppSelector(
      (state) => state.settings.personal.submitMessageWithEnter,
    );
    const language =
      useAppSelector(getCurrentConversationLanguage) ?? DEFAULT_LANG_CODE;

    const [uploads, setUploads] = useState<Record<string, FileUploadStatusT>>(
      {},
    );

    const [reduxForm] = useState<string>(() => {
      if (form) {
        return form;
      }
      formIdCounter++;
      return `rich-text-field-${formIdCounter}`;
    });

    const [editorPlainText, setEditorPlainText] = useState<string>('');

    const isUploading = useMemo(() => {
      return Object.values(uploads).some((u) => u.status === 'loading');
    }, [uploads]);

    useEffect(() => {
      if (typeof onFileIsUploadingChange === 'function') {
        onFileIsUploadingChange(isUploading);
      }
    }, [isUploading, onFileIsUploadingChange]);

    // WhatsApp exclusive mode (text OR attachment, not both)
    const {
      hasAttachments,
      isAttachmentsDisabledByText,
      isTextDisabledByAttachment,
      attachmentsDisabledTooltip,
      textDisabledPlaceholder,
    } = useWhatsAppExclusiveMode({
      channel,
      isWhatsAppAttachmentsEnabled:
        flags.conversationWhatsappOutboundAttachments,
      editorPlainText,
      attachments,
    });

    //#endregion

    //#reqion Publicly exposed methods
    const getText = useCallback((): string => {
      const editor = editorRef.current;
      if (!editor) {
        return '';
      }
      return editor.getSanitizedHtml();
    }, []);

    const getPlainText = useCallback((): string => {
      const editor = editorRef.current;
      if (!editor) {
        return '';
      }
      return editor.getPlainText();
    }, []);

    const setText = useCallback((text: string): void => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }
      editor.setHtml(text);
    }, []);

    const setMarkdown = useCallback(
      (markdown: string): void => {
        const markup = markdownToHTML(EmojiOne.shortnameToUnicode(markdown));
        const fixParagraphs = markup
          .replace(/<p>/g, '<div>')
          .replace(/<\/p>/g, '</div><br />');
        setText(fixParagraphs);
      },
      [setText],
    );

    const focus = useCallback((): void => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }

      editor.focus();
    }, []);

    const moveCursorToEnd = useCallback((): void => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }

      editor.moveCursorToEnd();
    }, []);

    useImperativeHandle(ref, () => {
      return {
        getText,
        getPlainText,
        setText,
        setMarkdown,
        focus,
        moveCursorToEnd,
      };
    }, [getText, getPlainText, setText, setMarkdown, focus, moveCursorToEnd]);
    //#endregion

    //#region Event handlers
    const handleToggleSendOnEnter = useCallback((): void => {
      dispatch(
        personalSubmitMessageWithEnter({
          key: 'submitMessageWithEnter',
          value: !submitMessageWithEnter,
        }),
      );
    }, [dispatch, submitMessageWithEnter]);

    const handleTriggerSend = useCallback((): void => {
      if (typeof onTriggerSend === 'function') {
        onTriggerSend();
      }
    }, [onTriggerSend]);

    const handleChange = useCallback((): void => {
      // wrap in setTimeout, because the event is fired before the inner state of the editor is updated
      setTimeout(() => {
        const text = getText();
        const plainText = getPlainText();

        setEditorPlainText(plainText);

        if (typeof onChange === 'function') {
          onChange(text);
        }
      }, 1);
    }, [onChange, getText, getPlainText]);

    const handleInlineUpload = useCallback(
      (file: File, uid: string): void => {
        setUploads((prevUploads) => ({
          ...prevUploads,
          [uid]: { status: 'loading', filename: file.name },
        }));

        if (file.size > MAX_INLINE_FILE_SIZE) {
          if (setValidationState) {
            setValidationState({
              touched: true,
              valid: true,
              warning: {
                type: 'file-size',
                arg: {
                  size: getFileSize(file.size),
                  maxSize: getFileSize(MAX_INLINE_FILE_SIZE),
                },
              },
            });

            setUploads((prevUploads) => ({
              ...prevUploads,
              [uid]: { status: 'failed', filename: file.name },
            }));
          }
        } else {
          dispatch(
            filesApi.endpoints.createAttachment.initiate({
              access: 'public',
              file: file,
            }),
          )
            .unwrap()
            .then((response) => {
              setUploads((prevUploads) => ({
                ...prevUploads,
                [uid]: {
                  status: 'succeeded',
                  link: response.data.absolute_uri,
                  filename: file.name,
                },
              }));
            })
            .catch((exception: unknown) => {
              setUploads((prevUploads) => ({
                ...prevUploads,
                [uid]: { status: 'failed', filename: file.name },
              }));
              console.warn('handleInlineUpload exception', exception);
            });
        }
      },
      [dispatch, setValidationState],
    );

    const handleAttachmentUpload = useCallback(
      (file: File, uid: string): void => {
        // WhatsApp-specific validation
        if (
          channel === 'whatsapp' &&
          flags.conversationWhatsappOutboundAttachments
        ) {
          const error = validateWhatsAppAttachment({
            file,
            existingAttachmentsCount: Array.isArray(attachments)
              ? attachments.length
              : 0,
          });

          if (error) {
            setValidationState?.({
              touched: true,
              valid: true,
              warning: error,
            });
            return;
          }
        }

        const totalSize =
          Array.isArray(attachments) && attachments.length > 0
            ? attachments
                .map((a) => a.size)
                .filter((size: number) => !isNaN(size))
                .reduce((sum: number, current: number) => sum + current, 0)
            : 0;

        if (MAX_ATTACHMENTS_TOTAL_FILE_SIZE > totalSize + file.size) {
          setUploads((prevUploads) => ({
            ...prevUploads,
            [uid]: { status: 'loading' },
          }));

          dispatch(
            filesApi.endpoints.createAttachment.initiate({
              file,
              access: [
                'adachat',
                'digitalgeniuschat',
                'kindlychat',
                'genericapimessaging',
                'widgetchat',
                'messenger',
                'whatsapp',
              ].includes(channel)
                ? 'public'
                : 'private',
            }),
          )
            .unwrap()
            .then((response) => {
              const attachment = {
                name: file.name,
                absolute_uri: response.data.absolute_uri,
                relative_uri: response.data.relative_uri,
                size: file.size,
                uid: uid,
              };
              setUploads((prevUploads) => ({
                ...prevUploads,
                [uid]: {
                  status: 'succeeded',
                  link: response.data.absolute_uri,
                },
              }));
              dispatch(arrayPush(reduxForm, 'attachments', attachment));
              // Remove file validation warnings after successful upload
              setValidationState?.({
                touched: true,
                valid: true,
                warning: undefined,
              });
            })
            .catch((exception: unknown) => {
              setUploads((prevUploads) => ({
                ...prevUploads,
                [uid]: { status: 'failed' },
              }));

              console.warn('handleAttachmentUpload exception', exception);
            });
        } else {
          console.warn(
            `Total attachment size is too large: (size: ${getFileSize(
              totalSize + file.size,
            )}, max ${getFileSize(MAX_ATTACHMENTS_TOTAL_FILE_SIZE)}.`,
          );

          setValidationState?.({
            touched: true,
            valid: true,
            warning: {
              type: 'file-size',
              arg: {
                size: getFileSize(totalSize + file.size),
                maxSize: getFileSize(MAX_ATTACHMENTS_TOTAL_FILE_SIZE),
              },
            },
          });
        }
      },
      [
        dispatch,
        channel,
        reduxForm,
        setUploads,
        setValidationState,
        attachments,
        flags.conversationWhatsappOutboundAttachments,
      ],
    );

    const handleInputError = useCallback((error: Error): void => {
      if (error.name === 'IndexSizeError') {
        Sentry.captureException(error, {
          tags: { team: SentryTeam.Core },
        });
        return;
      }
      throw error;
    }, []);

    //#endregion

    //#region calculated props passed to InputRichText

    const inputKeybindings = useMemo(() => {
      return Object.fromEntries(
        Object.entries(keybindings)
          .filter(([commandName]) =>
            commandName.startsWith(richTextEditorGroup),
          )
          .map(([commandName, keyCombinations]) => {
            return [
              commandName.replace(
                `${richTextEditorGroup}${commandGroupDelimiter}`,
                '',
              ),
              keyCombinations.map((keyCombination) => keyCombination.binding),
            ];
          }),
      ) as InputRichTextProps['keybindings'];
    }, [keybindings]);

    const inputHideSendOnEnterToggle = useMemo(() => {
      return (
        ['email', 'contactform', 'notes'].includes(channel) ||
        hideSendOnEnterToggle
      );
    }, [channel, hideSendOnEnterToggle]);

    const inputSendOnEnter = useMemo(() => {
      return (
        !disableSubmitMessageWithEnter &&
        submitMessageWithEnter &&
        !['email', 'contactform', 'notes', 'bulk'].includes(channel)
      );
    }, [disableSubmitMessageWithEnter, submitMessageWithEnter, channel]);

    const inputAllowFormatting: boolean | FormattingOption[] = useMemo(() => {
      // WhatsApp with feature flag support - custom formatting (no headings, no code blocks)
      if (channel === 'whatsapp' && flags.conversationWhatsappRichMessages) {
        return ['bold', 'italic', 'link', 'orderedList', 'unorderedList'];
      }

      return [
        'email',
        'contactform',
        'widgetchat',
        'notes',
        'messenger',
      ].includes(channel);
    }, [channel, flags.conversationWhatsappRichMessages]);

    const inputPlaceholderText = useMemo(() => {
      const suffix = inputSendOnEnter ? ' (send with <enter>)' : '';
      return placeholder
        ? placeholder
        : requesterName
          ? `Message ${requesterName}${suffix}`
          : `Type your message${suffix}`;
    }, [placeholder, requesterName, inputSendOnEnter]);

    const inputLanguages = useMemo(() => {
      if (!flags.templatesLanguageTranslationControl) {
        return undefined;
      }

      return supportedLanguages.concat().sort((a, b) => {
        if (a.code === language) {
          return -1;
        }
        if (b.code === language) {
          return 1;
        }
        return 0;
      });
    }, [flags.templatesLanguageTranslationControl, language]);

    const inputAllowInlineAttachments = useMemo(() => {
      return (
        ['email', 'contactform', 'notes'].includes(channel) &&
        !disableInlineAttachments
      );
    }, [channel, disableInlineAttachments]);

    const inputAllowAttachments = useMemo(() => {
      const channelsWithAttachmentSupport: Channel[] = [
        'adachat',
        'digitalgeniuschat',
        'kindlychat',
        'genericapimessaging',
        'email',
        'contactform',
        'widgetchat',
        'messenger',
      ];

      if (flags.conversationWhatsappOutboundAttachments) {
        // WhatsApp with feature flag support
        channelsWithAttachmentSupport.push('whatsapp');
      }

      return (
        !disableAttachments && channelsWithAttachmentSupport.includes(channel)
      );
    }, [
      channel,
      disableAttachments,
      flags.conversationWhatsappOutboundAttachments,
    ]);

    const inputSanitize = useMemo(() => {
      if (typeof sanitize === 'function') {
        return sanitize;
      }

      let sanitizationProfile = channelToSanitizationProfile[channel];

      if (sanitizationProfile === 'email' && !flags.writeHtmlEmail) {
        sanitizationProfile = 'widgetchat';
      }

      return SanitizeHTML.sanitizerForProfile(sanitizationProfile);
    }, [flags.writeHtmlEmail, channel, sanitize]);

    //#endregion

    return (
      <div className={styles.editor}>
        {(required || (!required && label)) && (
          <label className="control-label" htmlFor={undefined}>
            {label} {required ? '*' : ''}
          </label>
        )}
        {validation && (
          <StateBar
            channel={channel}
            text={editorPlainText}
            validation={validation}
            className={stateBarClassName}
            isPublicReply={isPublicReply}
            allowEmptyText={
              channel === 'whatsapp' &&
              flags.conversationWhatsappOutboundAttachments &&
              hasAttachments
            }
          />
        )}

        <InputRichText
          id={id}
          name=""
          value={value ?? ''}
          keybindings={inputKeybindings}
          editorWindow={editorWindow}
          onKeyDown={onKeyDown}
          autocomplete={autocomplete}
          wrapperClassName={classnames(styles.editorWrapper, {
            [styles.editorWrapperNoBorder]: !hasBorder,
          })}
          className={classnames(className, styles.editorRoot)}
          ref={editorRef}
          languages={inputLanguages}
          toolbarAddons={toolbarAddons}
          sendOnEnter={inputSendOnEnter}
          hideSendOnEnterToggle={inputHideSendOnEnterToggle}
          placeholder={textDisabledPlaceholder ?? inputPlaceholderText}
          fileUploadStatuses={uploads}
          allowFormatting={inputAllowFormatting}
          allowInlineAttachments={inputAllowInlineAttachments}
          allowAttachments={inputAllowAttachments}
          attachmentsDisabled={isAttachmentsDisabledByText}
          attachmentsDisabledTooltip={attachmentsDisabledTooltip}
          textInputDisabled={isTextDisabledByAttachment}
          allowEmoji={!disableEmoji}
          attachmentsFields={attachmentsFields}
          isAttachmentsUploading={isUploading}
          attachments={attachments}
          sanitize={inputSanitize}
          onChange={handleChange}
          onTriggerSend={handleTriggerSend}
          onToggleSendOnEnter={handleToggleSendOnEnter}
          onFileDropped={handleInlineUpload}
          onAttachmentsFile={handleAttachmentUpload}
          onError={handleInputError}
        />
      </div>
    );
  },
);

const richTextEditorGroup: CommandGroup = 'richTextEditor';
export default RichTextField;
